import { Media } from '@/components/Media'
import RichText from '@/components/RichText'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/utilities/ui'
import { ChevronRight } from 'lucide-react'
import React from 'react'

const FaqSections3 = ({
  questions,
  title,
  subtitle,
  image,
  addSchema = false,
}: {
  questions: any
  subtitle: any
  title: any
  image: any
  addSchema?: boolean
}) => {
  // Helper function to extract plain text from richText for schema
  const extractTextContent = (content: any): string => {
    if (typeof content === 'string') return content
    if (content && typeof content === 'object' && content.root) {
      try {
        const extractText = (node: any): string => {
          if (typeof node === 'string') return node
          if (node?.text) return node.text
          if (node?.children) {
            return node.children.map(extractText).join('')
          }
          return ''
        }
        return extractText(content)
      } catch {
        return ''
      }
    }
    return ''
  }

  // Generate FAQPage JSON-LD schema
  const faqSchema = addSchema
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions?.map((item: any) => ({
          '@type': 'Question',
          name: extractTextContent(item.question),
          acceptedAnswer: {
            '@type': 'Answer',
            text: extractTextContent(item.answer),
          },
        })),
      }
    : null
  return (
    <main className="container mx-auto rounded-2xl not-prose" aria-label="FAQ">
      {addSchema && faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <section
        className={
          image ? 'flex flex-col lg:flex-row gap-10 lg:items-center' : 'flex flex-col gap-10'
        }
        aria-label="Faq Content"
      >
        {image && (
          <aside className="w-full flex">
            <Media resource={image} imgClassName="" />
          </aside>
        )}
        <section className={image ? 'w-full' : 'w-full max-w-2xl'} aria-label="Faq List">
          <header className={image ? 'text-center mb-4' : 'text-center mb-4'}>
            {title && (
              <RichText
                className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 text-foreground text-center border border-border px-4 py-2 rounded-full"
                data={title}
                enableGutter={false}
                enableProse={false}
                resetChildMargins
              />
            )}
            {subtitle && (
              <RichText
                className="text-lg text-muted-foreground mb-2 text-center"
                data={subtitle}
                enableGutter={false}
                enableProse={false}
                resetChildMargins
              />
            )}
          </header>
          <Accordion type="single" collapsible className="w-full grid grid-cols-1 gap-6">
            {questions?.map((item: any, index: any) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-none">
                <Card className="rounded-3xl md:rounded-full [&:has([data-state=open])]:rounded-2xl md:[&:has([data-state=open])]:rounded-3xl overflow-hidden">
                  <AccordionTrigger
                    className="px-4 py-3 flex items-center text-foreground text-base [font-family:'Open_Sans',Helvetica] leading-[17.6px] [&[data-state=open]>span]:rotate-90  [&[data-state=open]>span]:bg-primary hover:no-underline"
                    hideIcon={true}
                    asDiv={true}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="flex items-center justify-center w-[45px] h-[45px] text-[16px] font-bold rounded-full bg-slate-300 text-primary transition-all duration-200 delay-100">
                        {index < 9 ? `0${index + 1}` : index + 1}
                      </div>
                      <div className="flex-1">
                        {item.question ? (
                          <RichText
                            className="text-xl font-semibold text-left text-foreground"
                            data={item.question}
                            enableGutter={false}
                            enableProse={false}
                            resetChildMargins
                          />
                        ) : (
                          <span className="text-foreground font-medium">
                            Question text not available
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="flex items-center justify-center w-[50px] h-[50px] text-[16px] font-bold text-center rounded-full transition-all duration-500 bg-slate-400 text-white">
                      <ChevronRight className="text-white transition-transform duration-200" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <CardContent className="px-4 py-4 [font-family:'Open_Sans',Helvetica] font-normal text-foreground text-base leading-[26px]">
                      {/* Example: Add internal anchor link to a service area page */}
                      {/* <a href="/services/fencing#dallas">Learn more about Dallas Fencing</a> */}
                      {item.answer ? (
                        <RichText
                          className="mb-0 text-foreground prose-headings:text-foreground prose-p:text-foreground prose-p:mb-2"
                          data={item.answer}
                          enableGutter={false}
                          enableProse={true}
                        />
                      ) : (
                        <span className="text-foreground">Answer text not available</span>
                      )}
                    </CardContent>
                  </AccordionContent>
                </Card>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </section>
    </main>
  )
}

export default FaqSections3
